import { SharingMessage } from '@eversports/klimt-patterns/SharingActions'
import { differenceInMinutes } from 'date-fns'
import { useSettingsContext } from '@eversports/react-app-base/SettingsContext'
import { useLocation } from 'react-router-dom'

import { useIntl } from '../../../localization/react'
import getPhoenixUrlParams from '../../../lib/get-phoenix-url-params'
import { useUseSharingMessageQuery } from '../../../graphql'
import formatDate from './helpers/format-date'

const useSharingMessage = (): SharingMessage => {
  const intl = useIntl()
  const location = useLocation()
  const { language, host } = useSettingsContext()
  const { orderId } = getPhoenixUrlParams(location.search)
  const { data } = useUseSharingMessageQuery({ variables: { orderId: orderId! } })

  if (
    !data ||
    data.order.items[0].__typename !== 'BookingOrderItem' ||
    data.order.items[0].booking.__typename !== 'EventBooking'
  ) {
    return { encodedMessage: '', translatedMessage: '', linkToCopy: '' }
  }

  const { booking } = data.order.items[0]
  const url = `${host}/activity/${booking.bookableItemV2.id}`
  const activityName = booking.bookableItemV2.activityName
  const venueName = booking.bookableItemV2.venue.name

  if (booking.purchasedWith?.__typename === 'Ticket' && booking.purchasedWith.validForSessions.length > 1) {
    const numberOfSessions = booking.purchasedWith.validForSessions.length
    const multiSessionStartFormattedDate = formatDate({
      date: booking.purchasedWith.validForSessions[0].start,
      language,
    })
    const multiSessionEndFormattedDate = formatDate({
      date: booking.purchasedWith.validForSessions[numberOfSessions - 1].start,
      language,
    })

    const multiSessionParams = {
      activityName: booking.bookableItemV2.activityName,
      venueName: booking.bookableItemV2.venue.name,
      startDate: multiSessionStartFormattedDate,
      endDate: multiSessionEndFormattedDate,
      numberOfSessions,
      url,
    }

    // Message that is shared:
    // I've booked this on Eversports. Come join me!
    //
    // {activityName} @ {venueName}
    // {$startDate} - ${endData} • {numberOfSessions} sessions
    //
    // ➡️ ${url}
    const translatedMessage = intl.multiSessionSharingMessage(multiSessionParams)

    return {
      encodedMessage: encodeURIComponent(translatedMessage),
      translatedMessage,
      linkToCopy: url,
    }
  }

  const singleSessionStart = booking.bookableItemV2.eventSessions[0].start
  const singleSessionEnd = booking.bookableItemV2.eventSessions[0].end
  const singleSessionStartDate = new Date(singleSessionStart)
  const singleSessionEndDate = new Date(singleSessionEnd)
  const singleSessionFormattedDate = formatDate({ date: singleSessionStart, language })

  const duration = differenceInMinutes(singleSessionEndDate, singleSessionStartDate)
  const startTime = singleSessionStartDate.toLocaleTimeString(language, {
    hour: '2-digit',
    minute: '2-digit',
  })

  const endTime = singleSessionEndDate.toLocaleTimeString(language, {
    hour: '2-digit',
    minute: '2-digit',
  })

  const dayOfTheWeek = new Intl.DateTimeFormat(language, {
    weekday: 'long',
  }).format(singleSessionStartDate)

  const singleSessionParams = {
    activityName,
    venueName,
    dayOfTheWeek,
    formattedDate: singleSessionFormattedDate,
    startTime,
    endTime,
    duration,
    url,
  }

  // Message that is shared:
  // I've booked this on Eversports. Come join me!
  //
  // {activityName} @ {venueName}
  // {dayOfTheWeek}, {date}, {time} ({duration})
  //
  // ➡️ ${url}
  const translatedMessage = intl.singleSessionSharingMessage(singleSessionParams)

  return {
    encodedMessage: encodeURIComponent(translatedMessage),
    translatedMessage,
    linkToCopy: url,
  }
}

export default useSharingMessage
